import React, { FC, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Form } from 'antd';

// Shared UI Library Components
import {
  CustomButton,
  CustomIcon,
  EIconName,
  CustomTooltip,
  DiscardModal,
  SpinElement
} from '@ppmcore/seven-ppm-core-shared-components-react';

// Styles
import './permission-settings.scss';

// Components
import { CounsellorsList } from '../counsellors-list/counsellors-list';
import { SettingsOption } from './children/settings-option/settings-option';

// Store entities
import {
  clearPermissionSettings,
  getCompanyPermissionSettings, getWorkersPermissionSettings
} from '../../../../store/permission-settings/permission-settings.thunks';
import { getPermissionSettingsState } from '../../../../store/permission-settings/permission-settings.selectors';

// Models
import { ICompanyPermissionSettings } from '../../../../interfaces/permission-settings.interfaces';

type TPermissionSettingsProps = {};

export const PermissionSettings: FC<TPermissionSettingsProps> = (
  {}: TPermissionSettingsProps
) => {
  const [form] = Form.useForm<ICompanyPermissionSettings>();

  const [discardOpen, setDiscardOpen] = useState<boolean>(false);

  const {
    company_permission_settings,
    company_permission_setting_workers,
    total_all,
    loading
  } = useSelector(getPermissionSettingsState);

  const dispatch = useDispatch<any>();

  const loadPermissionSettings = async (): Promise<void> => {
    const {payload} = await dispatch(getCompanyPermissionSettings());
    if (!payload) return;
    form.setFieldsValue(payload);
  }

  const loadWorkersSettings = async (isLoadMore: boolean = false): Promise<void> => {
    await dispatch(getWorkersPermissionSettings({isLoadMore, offset: company_permission_setting_workers.length}));
  }

  const toggleDiscardModal = (): void => {
    setDiscardOpen(!discardOpen);
  }

  const onDiscardChangesHandler = (state: boolean): void => {
    if (state) {
      toggleDiscardModal();
      return;
    }
    toggleDiscardModal();
  }

  useEffect(() => {
    loadPermissionSettings();
    loadWorkersSettings();

    return () => {
      dispatch(clearPermissionSettings());
    }
  }, []);

  return (
    <div className="permission-settings">

      <DiscardModal
        open={discardOpen}
        onCancel={onDiscardChangesHandler}
        title={'Are you sure you want to discard changes?'}
      />

      <div className={`permission-settings--loader permission-settings--loader-${loading ? 'show' : 'hide'}`}>
        <SpinElement fullHeight={true}/>
      </div>

      <div className="permission-settings--header">
        <div className="header-title">
          <div className="header-title--label">
            Permission Settings
          </div>
        </div>
        <div className="header-description">
          <p>This section enables you to customize permissions for Consultants.<br/>
            These permissions dictate the rights and capabilities available to Consultants who provide services to your
            clients.</p>

          <p>You have the flexibility to grant these permissions individually to each Consultant or establish general
            permissions for all Consultants. This functionality empowers you to manage access to specific platform
            features
            and maintain alignment with your business policies.</p>

          <p>Please exercise care when configuring permissions and routinely assess them to ensure they remain in
            harmony
            with your business's evolving requirements, thus upholding the safety and efficiency of your operations on
            Paypertok.</p>
        </div>
      </div>

      <div className="permission-settings--body">

        {/*<div className="body-table">*/}
        {/*  <div className="body-table--header">*/}
        {/*    <div className="header-item header-item--label">*/}
        {/*      Allow Clients*/}
        {/*    </div>*/}
        {/*    <div className="header-item">*/}
        {/*      Anonimity <CustomTooltip title={ 'Enabling this permission allows clients to maintain anonymity during consultations.
Disabling this permission will restrict clients from using this feature, and their identities will be visible to Consultants during consultations.' }>*/}
        {/*      <CustomIcon size={ 'size-md' } name={ EIconName.Info }/>*/}
        {/*    </CustomTooltip>*/}
        {/*    </div>*/}
        {/*    /!*<div className="header-item"/>*!/*/}
        {/*    <div className="header-item"/>*/}
        {/*  </div>*/}
        {/*  <div className="body-table--content">*/}
        {/*    <div className="content-item">*/}
        {/*      <SettingsOption type={ 'clients' } company_permission_settings={ company_permission_settings }/>*/}
        {/*    </div>*/}
        {/*  </div>*/}
        {/*</div>*/}

        <div className="body-table">
          <div className="body-table--header">
            <div className="header-item header-item--label">
              Allow Consultants
            </div>
            {/*<div className="header-item">*/}
            {/*  Availability Editing <CustomTooltip title={ 'Enabling this permission allows Consultants to edit their availability for consultations.
Disabling this permission will restrict Consultants from making changes to their availability, and you will retain control over their consultation schedules.' }>*/}
            {/*  <CustomIcon size={ 'size-md' } name={ EIconName.Info }/>*/}
            {/*</CustomTooltip>*/}
            {/*</div>*/}
            <div className="header-item">
              Personal Data Editing <CustomTooltip title={'Enable this permission to allow Consultants to add or update their knowledge, skills, specialties, specializations, and experience descriptions in their profiles.\n' +
              'Keep disabled this permission to prevent Consultants from editing the profile information you\'ve added.\n' +
              'You\'ll always be able to modify any information, even if added by Consultants.'}>
              <CustomIcon size={'size-md'} name={EIconName.Info}/>
            </CustomTooltip>
            </div>
            <div className="header-item">
              Rates Viewing<CustomTooltip title={'Enabling this permission grants Consultants access to view their consultation rates.\n' +
              'By disabling this permission, you maintain control over the visibility of these rates, ensuring they remain confidential at your discretion.'}>
              <CustomIcon size={'size-md'} name={EIconName.Info}/>
            </CustomTooltip>
            </div>
          </div>
          <div className="body-table--content">
            <div className="content-item">
              <SettingsOption type={'counsellors'} company_permission_settings={company_permission_settings}/>
            </div>
            <CounsellorsList counsellors={company_permission_setting_workers}
                             canAllEditPD={!!company_permission_settings.is_worker_profile_edit_enabled}
                             canAllSeeRates={!!company_permission_settings.is_worker_rates_show_enabled}
              // canAllEditAvailability={ !!company_permission_settings.is_worker_availability_edit_enabled }
            />
          </div>
        </div>

        {total_all > company_permission_setting_workers.length && <div className="body-item body-item--actions">
          <CustomButton text={'See more'} loading={loading}
                        onClick={() => loadWorkersSettings(true)}/>
        </div>}

      </div>
    </div>
  );
}
